import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Save, 
  Loader2,
  Home,
  Info,
  Phone,
  Settings,
  Plus,
  Trash2,
  Image
} from 'lucide-react';
import { getAllSettings, updateSettings } from '@/lib/api';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';

const AdminSiteSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState('hero');

  const fetchSettings = async () => {
    try {
      const response = await getAllSettings();
      const settingsMap = {};
      response.data.forEach(item => {
        settingsMap[item.key] = item.value || {};
      });
      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Ayarlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    
    try {
      await updateSettings(key, { value: settings[key] });
      toast.success('Ayarlar başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Ayarlar güncellenirken hata oluştu');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const updateField = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateArrayField = (section, field, index, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section]?.[field]?.map((item, i) => 
          i === index ? (typeof item === 'string' ? value : { ...item, ...value }) : item
        ) || []
      }
    }));
  };

  const addArrayItem = (section, field, defaultItem) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...(prev[section]?.[field] || []), defaultItem]
      }
    }));
  };

  const removeArrayItem = (section, field, index) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section]?.[field]?.filter((_, i) => i !== index) || []
      }
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-site-settings">
        <div>
          <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Site Ayarları
          </h2>
          <p className="text-slate-600">Sitenin tüm içeriklerini buradan düzenleyebilirsiniz</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="hero" className="gap-1 text-xs">
              <Home className="w-4 h-4" />
              Ana Sayfa
            </TabsTrigger>
            <TabsTrigger value="services_hero" className="gap-1 text-xs">
              <Settings className="w-4 h-4" />
              Hizmetler
            </TabsTrigger>
            <TabsTrigger value="about_hero" className="gap-1 text-xs">
              <Info className="w-4 h-4" />
              Hakkımızda
            </TabsTrigger>
            <TabsTrigger value="about" className="gap-1 text-xs">
              <Info className="w-4 h-4" />
              Değerler
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-1 text-xs">
              <Phone className="w-4 h-4" />
              İletişim
            </TabsTrigger>
            <TabsTrigger value="cta" className="gap-1 text-xs">
              <Settings className="w-4 h-4" />
              CTA
            </TabsTrigger>
            <TabsTrigger value="footer" className="gap-1 text-xs">
              <Settings className="w-4 h-4" />
              Footer
            </TabsTrigger>
          </TabsList>

          {/* Hero Settings */}
          <TabsContent value="hero">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#005CB2]" />
                  Ana Sayfa (Hero) Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rozet Metni</Label>
                    <Input
                      data-testid="hero-badge-input"
                      value={settings.hero?.badge || ''}
                      onChange={(e) => updateField('hero', 'badge', e.target.value)}
                      placeholder="Profesyonel Danışmanlık Hizmetleri"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ana Başlık</Label>
                    <Input
                      data-testid="hero-title-input"
                      value={settings.hero?.title || ''}
                      onChange={(e) => updateField('hero', 'title', e.target.value)}
                      placeholder="Doğru İşe, Doğru İnsan Kaynağı"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Alt Başlık</Label>
                  <Textarea
                    data-testid="hero-subtitle-input"
                    value={settings.hero?.subtitle || ''}
                    onChange={(e) => updateField('hero', 'subtitle', e.target.value)}
                    rows={2}
                  />
                </div>

                {/* Stats */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">İstatistikler</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('hero', 'stats', { value: '', label: '' })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ekle
                    </Button>
                  </div>
                  {settings.hero?.stats?.map((stat, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Değer</Label>
                        <Input
                          value={stat.value || ''}
                          onChange={(e) => updateArrayField('hero', 'stats', index, { ...stat, value: e.target.value })}
                          placeholder="10+"
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Etiket</Label>
                        <Input
                          value={stat.label || ''}
                          onChange={(e) => updateArrayField('hero', 'stats', index, { ...stat, label: e.target.value })}
                          placeholder="Yıllık Deneyim"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeArrayItem('hero', 'stats', index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Images */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Hero Görselleri</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('hero', 'images', '')}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ekle
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {settings.hero?.images?.map((img, index) => (
                      <div key={index} className="relative p-3 bg-slate-50 rounded-lg">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('hero', 'images', index)}
                          className="absolute top-1 right-1 text-red-600 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 rounded-full z-10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        <ImageUploader
                          value={img || ''}
                          onChange={(newUrl) => {
                            const newImages = [...(settings.hero?.images || [])];
                            newImages[index] = newUrl;
                            updateField('hero', 'images', newImages);
                          }}
                          label={`Görsel ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => handleSave('hero')}
                  disabled={saving.hero}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.hero ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Hero Settings */}
          <TabsContent value="services_hero">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#005CB2]" />
                  Hizmetler Sayfası Giriş Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rozet Metni</Label>
                    <Input
                      value={settings.services_hero?.badge || ''}
                      onChange={(e) => updateField('services_hero', 'badge', e.target.value)}
                      placeholder="Profesyonel Çözümler"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Başlık</Label>
                    <Input
                      value={settings.services_hero?.title || ''}
                      onChange={(e) => updateField('services_hero', 'title', e.target.value)}
                      placeholder="Hizmetlerimiz"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea
                    value={settings.services_hero?.subtitle || ''}
                    onChange={(e) => updateField('services_hero', 'subtitle', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-semibold">Özellik 1</Label>
                    <Input
                      value={settings.services_hero?.feature1_title || ''}
                      onChange={(e) => updateField('services_hero', 'feature1_title', e.target.value)}
                      placeholder="Güvenilir"
                    />
                    <Input
                      value={settings.services_hero?.feature1_subtitle || ''}
                      onChange={(e) => updateField('services_hero', 'feature1_subtitle', e.target.value)}
                      placeholder="%100 Memnuniyet"
                    />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-semibold">Özellik 2</Label>
                    <Input
                      value={settings.services_hero?.feature2_title || ''}
                      onChange={(e) => updateField('services_hero', 'feature2_title', e.target.value)}
                      placeholder="Hızlı"
                    />
                    <Input
                      value={settings.services_hero?.feature2_subtitle || ''}
                      onChange={(e) => updateField('services_hero', 'feature2_subtitle', e.target.value)}
                      placeholder="Anında Çözüm"
                    />
                  </div>
                </div>

                {/* Hizmet Kartları Görselleri - Arka plan renkleri */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Hizmet Kartları Arka Plan
                  </Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">İnsan Kaynakları Kart Rengi</Label>
                      <Input
                        value={settings.services_hero?.card1_bg || ''}
                        onChange={(e) => updateField('services_hero', 'card1_bg', e.target.value)}
                        placeholder="bg-white/10 veya özel renk"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Dijital Baskı Kart Rengi</Label>
                      <Input
                        value={settings.services_hero?.card2_bg || ''}
                        onChange={(e) => updateField('services_hero', 'card2_bg', e.target.value)}
                        placeholder="bg-white/10 veya özel renk"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Araç Kiralama Kart Rengi</Label>
                      <Input
                        value={settings.services_hero?.card3_bg || ''}
                        onChange={(e) => updateField('services_hero', 'card3_bg', e.target.value)}
                        placeholder="bg-white/10 veya özel renk"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Eğitim Kart Rengi</Label>
                      <Input
                        value={settings.services_hero?.card4_bg || ''}
                        onChange={(e) => updateField('services_hero', 'card4_bg', e.target.value)}
                        placeholder="bg-white/10 veya özel renk"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => handleSave('services_hero')}
                  disabled={saving.services_hero}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.services_hero ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* About Hero Settings */}
          <TabsContent value="about_hero">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#005CB2]" />
                  Hakkımızda Sayfası Giriş Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Rozet Metni</Label>
                  <Input
                    value={settings.about_hero?.badge || ''}
                    onChange={(e) => updateField('about_hero', 'badge', e.target.value)}
                    placeholder="Kurumsal Kimlik"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-semibold">Kart 1</Label>
                    <Input
                      value={settings.about_hero?.card1_title || ''}
                      onChange={(e) => updateField('about_hero', 'card1_title', e.target.value)}
                      placeholder="Hedef Odaklı"
                    />
                    <Input
                      value={settings.about_hero?.card1_subtitle || ''}
                      onChange={(e) => updateField('about_hero', 'card1_subtitle', e.target.value)}
                      placeholder="Sonuç garantisi"
                    />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-semibold">Kart 2</Label>
                    <Input
                      value={settings.about_hero?.card2_title || ''}
                      onChange={(e) => updateField('about_hero', 'card2_title', e.target.value)}
                      placeholder="Güvenilir"
                    />
                    <Input
                      value={settings.about_hero?.card2_subtitle || ''}
                      onChange={(e) => updateField('about_hero', 'card2_subtitle', e.target.value)}
                      placeholder="İş ortağınız"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-semibold">Kart 3</Label>
                    <Input
                      value={settings.about_hero?.card3_title || ''}
                      onChange={(e) => updateField('about_hero', 'card3_title', e.target.value)}
                      placeholder="Profesyonel"
                    />
                    <Input
                      value={settings.about_hero?.card3_subtitle || ''}
                      onChange={(e) => updateField('about_hero', 'card3_subtitle', e.target.value)}
                      placeholder="Uzman kadro"
                    />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-semibold">Kart 4</Label>
                    <Input
                      value={settings.about_hero?.card4_title || ''}
                      onChange={(e) => updateField('about_hero', 'card4_title', e.target.value)}
                      placeholder="İnsan Odaklı"
                    />
                    <Input
                      value={settings.about_hero?.card4_subtitle || ''}
                      onChange={(e) => updateField('about_hero', 'card4_subtitle', e.target.value)}
                      placeholder="Değer veriyoruz"
                    />
                  </div>
                </div>

                {/* Neden Biz Bölümü Görselleri */}
                <div className="space-y-4 border-t pt-6 mt-6">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    "Neden Biz?" Bölümü Görselleri
                  </Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ImageUploader
                      value={settings.about_hero?.whyus_image1 || ''}
                      onChange={(url) => updateField('about_hero', 'whyus_image1', url)}
                      label="Görsel 1 (Sol Üst)"
                    />
                    <ImageUploader
                      value={settings.about_hero?.whyus_image2 || ''}
                      onChange={(url) => updateField('about_hero', 'whyus_image2', url)}
                      label="Görsel 2 (Sol Alt)"
                    />
                    <ImageUploader
                      value={settings.about_hero?.whyus_image3 || ''}
                      onChange={(url) => updateField('about_hero', 'whyus_image3', url)}
                      label="Görsel 3 (Sağ Üst)"
                    />
                    <ImageUploader
                      value={settings.about_hero?.whyus_image4 || ''}
                      onChange={(url) => updateField('about_hero', 'whyus_image4', url)}
                      label="Görsel 4 (Sağ Alt)"
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => handleSave('about_hero')}
                  disabled={saving.about_hero}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.about_hero ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* About Settings */}
          <TabsContent value="about">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#005CB2]" />
                  Hakkımızda Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Başlık</Label>
                    <Input
                      value={settings.about?.title || ''}
                      onChange={(e) => updateField('about', 'title', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alt Başlık</Label>
                    <Input
                      value={settings.about?.subtitle || ''}
                      onChange={(e) => updateField('about', 'subtitle', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Açıklama 1</Label>
                  <Textarea
                    value={settings.about?.description || ''}
                    onChange={(e) => updateField('about', 'description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Açıklama 2</Label>
                  <Textarea
                    value={settings.about?.description2 || ''}
                    onChange={(e) => updateField('about', 'description2', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Misyon</Label>
                  <Input
                    value={settings.about?.mission || ''}
                    onChange={(e) => updateField('about', 'mission', e.target.value)}
                  />
                </div>

                <ImageUploader
                  value={settings.about?.image || ''}
                  onChange={(url) => updateField('about', 'image', url)}
                  label="Hakkımızda Görseli"
                />

                {/* Values */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Değerlerimiz</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('about', 'values', { icon: 'Target', title: '', description: '' })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ekle
                    </Button>
                  </div>
                  {settings.about?.values?.map((val, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-2">
                      <div className="flex gap-2">
                        <div className="w-32 space-y-1">
                          <Label className="text-xs">İkon</Label>
                          <select
                            value={val.icon || 'Target'}
                            onChange={(e) => updateArrayField('about', 'values', index, { ...val, icon: e.target.value })}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="Target">Target</option>
                            <option value="Award">Award</option>
                            <option value="Users">Users</option>
                            <option value="Building2">Building2</option>
                            <option value="CheckCircle2">CheckCircle2</option>
                          </select>
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs">Başlık</Label>
                          <Input
                            value={val.title || ''}
                            onChange={(e) => updateArrayField('about', 'values', index, { ...val, title: e.target.value })}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('about', 'values', index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 self-end"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Açıklama</Label>
                        <Input
                          value={val.description || ''}
                          onChange={(e) => updateArrayField('about', 'values', index, { ...val, description: e.target.value })}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Milestones */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Tarihçe</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('about', 'milestones', { year: '', title: '', description: '' })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ekle
                    </Button>
                  </div>
                  {settings.about?.milestones?.map((mile, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="w-24 space-y-1">
                        <Label className="text-xs">Yıl</Label>
                        <Input
                          value={mile.year || ''}
                          onChange={(e) => updateArrayField('about', 'milestones', index, { ...mile, year: e.target.value })}
                        />
                      </div>
                      <div className="w-32 space-y-1">
                        <Label className="text-xs">Başlık</Label>
                        <Input
                          value={mile.title || ''}
                          onChange={(e) => updateArrayField('about', 'milestones', index, { ...mile, title: e.target.value })}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Açıklama</Label>
                        <Input
                          value={mile.description || ''}
                          onChange={(e) => updateArrayField('about', 'milestones', index, { ...mile, description: e.target.value })}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeArrayItem('about', 'milestones', index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Why Us */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Neden Biz? Maddeleri</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('about', 'whyUs', '')}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ekle
                    </Button>
                  </div>
                  {settings.about?.whyUs?.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        className="flex-1"
                        value={item || ''}
                        onChange={(e) => {
                          const newItems = [...(settings.about?.whyUs || [])];
                          newItems[index] = e.target.value;
                          updateField('about', 'whyUs', newItems);
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeArrayItem('about', 'whyUs', index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={() => handleSave('about')}
                  disabled={saving.about}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.about ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Settings */}
          <TabsContent value="contact">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#005CB2]" />
                  İletişim Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sayfa Başlığı</Label>
                    <Input
                      value={settings.contact?.title || ''}
                      onChange={(e) => updateField('contact', 'title', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Adres</Label>
                    <Input
                      value={settings.contact?.address || ''}
                      onChange={(e) => updateField('contact', 'address', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Sayfa Açıklaması</Label>
                  <Textarea
                    value={settings.contact?.subtitle || ''}
                    onChange={(e) => updateField('contact', 'subtitle', e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Adres Detayı</Label>
                    <Input
                      value={settings.contact?.addressDetail || ''}
                      onChange={(e) => updateField('contact', 'addressDetail', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefon</Label>
                    <Input
                      value={settings.contact?.phone || ''}
                      onChange={(e) => updateField('contact', 'phone', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefon Saatleri</Label>
                    <Input
                      value={settings.contact?.phoneHours || ''}
                      onChange={(e) => updateField('contact', 'phoneHours', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>E-posta</Label>
                    <Input
                      value={settings.contact?.email || ''}
                      onChange={(e) => updateField('contact', 'email', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>E-posta Notu</Label>
                    <Input
                      value={settings.contact?.emailNote || ''}
                      onChange={(e) => updateField('contact', 'emailNote', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Çalışma Saatleri</Label>
                    <Input
                      value={settings.contact?.workingHours || ''}
                      onChange={(e) => updateField('contact', 'workingHours', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Çalışma Saatleri Notu</Label>
                  <Input
                    value={settings.contact?.workingHoursNote || ''}
                    onChange={(e) => updateField('contact', 'workingHoursNote', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Google Harita Embed URL</Label>
                  <Input
                    value={settings.contact?.mapUrl || ''}
                    onChange={(e) => updateField('contact', 'mapUrl', e.target.value)}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                  />
                  <p className="text-xs text-slate-500">Google Maps'ten "Paylaş &gt; Haritayı Yerleştir" seçeneğinden kopyalayın</p>
                </div>

                <Button 
                  onClick={() => handleSave('contact')}
                  disabled={saving.contact}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.contact ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CTA Settings */}
          <TabsContent value="cta">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#005CB2]" />
                  CTA (Call to Action) Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Başlık</Label>
                  <Input
                    value={settings.cta?.title || ''}
                    onChange={(e) => updateField('cta', 'title', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Alt Başlık</Label>
                  <Textarea
                    value={settings.cta?.subtitle || ''}
                    onChange={(e) => updateField('cta', 'subtitle', e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Buton Metni</Label>
                  <Input
                    value={settings.cta?.buttonText || ''}
                    onChange={(e) => updateField('cta', 'buttonText', e.target.value)}
                  />
                </div>

                <Button 
                  onClick={() => handleSave('cta')}
                  disabled={saving.cta}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.cta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Settings */}
          <TabsContent value="footer">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#005CB2]" />
                  Footer Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4 border-b pb-6 mb-4">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Site Logosu
                  </Label>
                  <ImageUploader
                    value={settings.footer?.logoUrl || ''}
                    onChange={(url) => updateField('footer', 'logoUrl', url)}
                    label="Logo Görseli (Header için)"
                    placeholder="/images/eta-logo.png veya https://..."
                  />
                  <p className="text-xs text-slate-500">Boş bırakılırsa varsayılan logo kullanılır</p>
                </div>

                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea
                    value={settings.footer?.description || ''}
                    onChange={(e) => updateField('footer', 'description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Copyright Metni</Label>
                  <Input
                    value={settings.footer?.copyright || ''}
                    onChange={(e) => updateField('footer', 'copyright', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Şirket Adı (Logo Metni)</Label>
                  <Input
                    value={settings.footer?.companyName || ''}
                    onChange={(e) => updateField('footer', 'companyName', e.target.value)}
                    placeholder="ETA GLOBAL"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Şirket Alt Başlığı</Label>
                  <Input
                    value={settings.footer?.companySubtitle || ''}
                    onChange={(e) => updateField('footer', 'companySubtitle', e.target.value)}
                    placeholder="DANIŞMANLIK"
                  />
                </div>

                <Button 
                  onClick={() => handleSave('footer')}
                  disabled={saving.footer}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  {saving.footer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminSiteSettings;
